import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import classnames from 'clsx';

import { Text, TextType } from 'ui-components/components/text';

import style from './selectable-tile.module.css';

/**
 * @typedef {import('../../../prop-types/intl').MessageDescriptorPropType} MessageDescriptorPropType
 */

/**
 * @param {object} props - component props
 * @param {React.ReactNode} [props.children] - additional content displayed inside the tile
 * @param {MessageDescriptorPropType | string} [props.description] - description to be displayed
 * @param {boolean} [props.disabled] - indicates if tile is disabled
 * @param {boolean} [props.isCentered] - indicates if tile content must be centered
 * @param {boolean} [props.isSelected] - indicates if tile is selected
 * @param {boolean} [props.isReadOnly] - indicates if tile is used to inform
 * @param {string} [props.name] - name to identify the tile
 * @param {string} [props.className] - className to be applied to the container
 * @param {(name: string) => void} [props.onClick] - callback executed when tile is clicked
 * @param {MessageDescriptorPropType | string} props.title - title to be displayed
 * @returns {JSX.Element}
 */
export const SelectableTile = ({
  children,
  className = '',
  description,
  disabled = false,
  isCentered = false,
  isSelected = false,
  isReadOnly = false,
  name = '',
  onClick,
  title,
}) => {
  const handleOnClick = useCallback(() => {
    if (!disabled) {
      onClick?.(name);
    }
  }, [disabled, name, onClick]);

  const containerClassName = classnames(
    className,
    style.container,
    { [style.selected]: isSelected },
    { [style.centered]: isCentered },
    { [style.disabled]: disabled },
    { [style['read-only']]: isReadOnly },
  );

  return (
    <section className={containerClassName} onClick={handleOnClick}>
      <div className={style['title-container']}>
        <Text
          className={classnames(style.title, {
            [style['title-selected']]: isSelected,
          })}
          textType={TextType.H4}
          value={title}
        />
        {isSelected ? <FontAwesomeIcon className={style.check} icon={faCircleCheck} /> : null}
      </div>

      {description ? <Text className={style.description} textType={TextType.Caption} value={description} /> : null}

      {children ? <div className={style['additional-content']}>{children}</div> : null}
    </section>
  );
};
